import {
  BN,
  buildCreateMarket,
  buildPrepareOutcomes,
  deriveMarketPda,
  deriveOutcomeMintPda,
  isMetadataName,
  isMetadataSymbol,
  utf8ByteLength,
} from '@morfi/contract-sdk';
import { type Connection, PublicKey } from '@solana/web3.js';
import type { SigningDeps } from '../auth/turnkey/signing-types.js';
import type { TxEngineLogger } from '../tx-engine/config.js';
import {
  createTransactionTraceId,
  hashDiagnosticEntity,
} from '../tx-engine/diagnostics/lifecycle.js';
import { resolveRelayFeePayer } from '../tx-engine/relay/fee-payer.js';
import type { MarketEditorialCreateTransport } from '../tx-engine/relay/market-editorial.js';
import type { TurnkeyDeps } from '../tx-engine/turnkey-types.js';
import type { ActiveWallet, SubmissionOptions } from '../tx-engine/types.js';
import { sleepOrAbort, throwIfAborted } from './orchestrator-abort.js';
import { type SubmitTransactionFn, toEngineBuildResult } from './orchestrator-build.js';
import {
  baseMarketCreationMetadata,
  buildWithFailureLog,
  errorReason,
  isAbortError,
  operationalErrorClass,
  stageMetadata,
  submissionFailureLog,
} from './orchestrator-diagnostics.js';
import {
  assertEditorialCreatePreconditions,
  postEditorialCreateSnapshot,
} from './orchestrator-editorial.js';
import {
  createPreparedBitmapProof,
  createShellLandingProof,
  landedShellSignature,
} from './orchestrator-landing.js';
import { stageInitialOutcomeMetadata } from './orchestrator-staging.js';
import { type FailedSubmissionResult, retryTransientSubmission } from './retry-submission.js';
import { txSubmitError } from './tx-submit-error.js';
import {
  type CreateMarketOutcomeParams,
  type CreateMarketParams,
  type CreateMarketProgressState,
  type CreateMarketResult,
  INITIAL_PROGRESS_STATE,
} from './types.js';

const NOOP_LOGGER: TxEngineLogger = {
  info: (): void => {},
  warn: (): void => {},
  error: (): void => {},
};

export interface RunMarketCreationArgs {
  /** Solana connection passed to the build-only contract SDK. */
  readonly connection: Connection;
  /** Host-bound submit function (from `useSubmitTransaction(deps)`). */
  readonly submitTransaction: SubmitTransactionFn;
  /** User's wallet handle — Turnkey / external. */
  readonly activeWallet: ActiveWallet;
  /** Turnkey signing deps (always present per data-model.md). */
  readonly signingDeps: SigningDeps;
  /** Turnkey submission deps (always present, used by the engine internals). */
  readonly turnkeyDeps: TurnkeyDeps;
  /** Market-creation parameters (ticker, outcomes, marketConfig, quoteMint, …). */
  readonly params: CreateMarketParams;
  /** Optional progress callback — fires after every meaningful state change. */
  readonly onProgress?: (snapshot: CreateMarketProgressState) => void;
  /** Optional abort signal — cancels any pending stage cleanly. */
  readonly signal?: AbortSignal;
  /** Optional logger — defaults to no-op. */
  readonly logger?: TxEngineLogger;
  /** Optional transport for the editorial `POST /v1/markets` pre-publish step. */
  readonly editorialCreateTransport?: MarketEditorialCreateTransport;
}

/**
 * Pick a fresh u64 market nonce. Uses crypto.getRandomValues so the value
 * is unpredictable across browser sessions; the on-chain check is that
 * `(creator, market_nonce)` resolves to an uninitialized PDA, so any
 * unused 64-bit value works.
 */
function freshMarketNonce(): BN {
  const buf = new Uint8Array(8);
  crypto.getRandomValues(buf);
  return new BN(buf, 'le');
}

/**
 * Run the full market-creation lifecycle. Resolves with the final result
 * (signatures + market pubkey) on success; throws on first stage failure.
 * The orchestrator emits a progress snapshot before and after every
 * stage so the UI can render the strip in real time.
 */
export async function runMarketCreation(args: RunMarketCreationArgs): Promise<CreateMarketResult> {
  const { connection, submitTransaction, activeWallet, signingDeps, turnkeyDeps, params, signal } =
    args;
  const onProgress = args.onProgress ?? ((): void => {});
  const logger = args.logger;
  assertEditorialCreatePreconditions(params);

  const creator = new PublicKey(activeWallet.address);
  const marketConfig = new PublicKey(params.marketConfigBase58);
  const quoteMint = new PublicKey(params.quoteMintBase58);
  const marketNonce =
    params.marketNonceDecimal !== undefined
      ? new BN(params.marketNonceDecimal, 10)
      : freshMarketNonce();
  const marketCreationTraceId = createTransactionTraceId();

  let state: CreateMarketProgressState = {
    ...INITIAL_PROGRESS_STATE,
    status: 'preparing',
    outcomesTotal: params.outcomes.length,
  };
  onProgress(state);

  const baseOpts = {
    signingDeps,
    turnkeyDeps,
    activeWallet,
    ...(signal !== undefined ? { signal } : {}),
  };

  const [marketPda] = deriveMarketPda(creator, marketNonce);
  const marketPubkey = marketPda.toBase58();
  const outcomeMints = params.outcomes.map((_, index) => deriveOutcomeMintPda(marketPda, index)[0]);
  const baseMetadata = baseMarketCreationMetadata({
    marketCreationTraceId,
    marketPubkey,
    creatorPubkey: creator.toBase58(),
    marketNonceDecimal: marketNonce.toString(10),
    ticker: params.ticker,
    outcomeCount: params.outcomes.length,
  });

  for (const [outcomeIndex, outcome] of params.outcomes.entries()) {
    if (!isMetadataName(outcome.name) || !isMetadataSymbol(outcome.symbol)) {
      logger?.warn('market_creation.metadata_text_invalid', {
        outcomeIndex,
        nameBytes: utf8ByteLength(outcome.name),
        symbolBytes: utf8ByteLength(outcome.symbol),
      });
      throw new Error('Outcome metadata requires a name of 1–32 and symbol of 1–10 UTF-8 bytes');
    }
  }

  let relayFeePayerBase58: string;
  let relayPayer: PublicKey;
  try {
    relayFeePayerBase58 = await resolveRelayFeePayer(logger ?? NOOP_LOGGER);
    relayPayer = new PublicKey(relayFeePayerBase58);
  } catch (err) {
    const message = errorReason(err);
    state = {
      ...state,
      status: 'failed',
      marketPubkey,
      error: {
        stage: 'preparing',
        message,
      },
    };
    onProgress(state);
    logger?.error('market_creation.relay_fee_payer_resolve_failed', {
      ...stageMetadata(baseMetadata, 'preparing', 'resolve_relay_fee_payer'),
      errorClass: operationalErrorClass(err),
      errorCode: 'MC_RELAY_FEE_PAYER_RESOLVE_FAILED',
      errorStage: 'preparing',
    });
    throw err;
  }

  let preparedOutcomes: readonly CreateMarketOutcomeParams[];
  try {
    throwIfAborted(signal);
    preparedOutcomes = await postEditorialCreateSnapshot({
      params,
      activeWallet,
      marketNonce,
      marketPubkey,
      outcomeMints,
      transport: args.editorialCreateTransport,
      signal,
    });
  } catch (err) {
    if (isAbortError(err)) {
      throw err;
    }
    const message = errorReason(err);
    state = {
      ...state,
      status: 'failed',
      marketPubkey,
      error: {
        stage: 'preparing',
        message,
      },
    };
    onProgress(state);
    logger?.error('market_creation.editorial_create_failed', {
      ...stageMetadata(baseMetadata, 'editorial', 'post_market_editorial', {
        idempotencyKeyHash: hashDiagnosticEntity(`create-market-editorial:${marketPubkey}`),
      }),
      errorClass: operationalErrorClass(err),
      errorCode: 'MC_EDITORIAL_CREATE_FAILED',
      errorStage: 'editorial',
    });
    throw err;
  }

  try {
    await stageInitialOutcomeMetadata({
      connection,
      submitTransaction,
      creator,
      payer: relayPayer,
      market: marketPda,
      marketNonce,
      outcomes: preparedOutcomes,
      baseOpts,
      relayFeePayerBase58,
      baseMetadata,
      marketCreationTraceId,
      signal,
      logger,
    });
  } catch (error) {
    state = {
      ...state,
      status: 'failed',
      error: { stage: 'preparing', message: errorReason(error) },
    };
    onProgress(state);
    throw error;
  }

  // ── Stage 1: create_market ─────────────────────────────────────────
  throwIfAborted(signal);
  const shellStageMetadata = stageMetadata(baseMetadata, 'shell', 'create_market');
  const shellBuild = await buildWithFailureLog({
    logger,
    metadata: shellStageMetadata,
    build: () =>
      buildCreateMarket(connection, {
        creator,
        payer: relayPayer,
        marketConfig,
        marketNonce,
        nOutcomes: params.outcomes.length,
        quoteMint,
        initialBacking: new BN(params.initialBackingD6 ?? '0', 10),
        configAdmin: creator,
      }),
  });
  state = {
    ...state,
    status: 'shell',
    marketPubkey,
  };
  onProgress(state);

  const shellTraceId = createTransactionTraceId();
  const shellMetadata = {
    ...shellStageMetadata,
    txTraceId: shellTraceId,
  };
  const shellOpts: SubmissionOptions = {
    ...baseOpts,
    instructionName: 'create_market',
    traceId: shellTraceId,
    metadata: shellMetadata,
    forceRelay: true,
    expectedRelayFeePayer: relayFeePayerBase58,
  };
  const shellLanded = createShellLandingProof(
    connection,
    marketPda,
    params.outcomes.length,
    logger,
  );
  const shellResult = await retryTransientSubmission({
    submitTransaction,
    buildResult: toEngineBuildResult(shellBuild),
    options: shellOpts,
    retryContext: { workflow: 'market_creation', workflowTraceId: marketCreationTraceId },
    signal,
    throwIfAborted,
    sleepOrAbort,
    alreadyLanded: shellLanded,
    onRetryScheduled: ({ attempt, delayMs, result }) =>
      logger?.warn('market_creation.stage_retry_scheduled', {
        ...submissionFailureLog(shellMetadata, result),
        attempt,
        delayMs,
      }),
  });
  const failShell = (result: FailedSubmissionResult): never => {
    state = {
      ...state,
      status: 'failed',
      error: {
        stage: 'shell',
        message: result.error.morfiError ?? result.error.userMessageKey ?? 'shell submit failed',
      },
    };
    onProgress(state);
    logger?.error('market_creation.shell_failed', submissionFailureLog(shellMetadata, result));
    throw txSubmitError(
      `create_market failed: ${result.error.morfiError ?? result.error.userMessageKey ?? result.error.code}`,
      result.error,
      submissionFailureLog(shellMetadata, result),
    );
  };
  const shellSignature = shellResult.ok
    ? shellResult.signature
    : (await shellLanded())
      ? await landedShellSignature(connection, marketPda, logger)
      : failShell(shellResult);
  state = {
    ...state,
    shellSubmitted: shellSignature,
    shellConfirmed: true,
    status: 'outcomes',
  };
  onProgress(state);

  // ── Stage 2: prepare_outcomes × N (sequential) ───────────────────────────────
  const outcomeSignatures: string[] = [];
  const provenPreparedBitmap = createPreparedBitmapProof(
    connection,
    marketPda,
    preparedOutcomes.length,
    logger,
  );
  let externalPreparationBitmap = 0;
  for (let i = 0; i < params.outcomes.length; i += 1) {
    throwIfAborted(signal);
    if ((externalPreparationBitmap & (1 << i)) !== 0) {
      state = { ...state, outcomesConfirmed: i + 1 };
      onProgress(state);
      continue;
    }
    const outcome = preparedOutcomes[i];
    const outcomeMint = outcomeMints[i];
    if (outcome === undefined || outcomeMint === undefined) {
      throw new Error(`internal: missing outcome data at index ${String(i)}`);
    }
    const outcomeStageMetadata = stageMetadata(baseMetadata, 'outcome', 'prepare_outcomes', {
      outcomeIndex: i,
      outcomeMintHash: hashDiagnosticEntity(outcomeMint.toBase58()),
      outcomeTickerHash: hashDiagnosticEntity(outcome.ticker),
    });
    const outcomeBuild = await buildWithFailureLog({
      logger,
      metadata: outcomeStageMetadata,
      build: () =>
        buildPrepareOutcomes(connection, {
          creator,
          payer: relayPayer,
          requireCreatorSignature: true,
          market: marketPda,
          marketConfig,
          eventSchedule: null,
          outcomes: [
            { outcomeIndex: i, name: outcome.name, symbol: outcome.symbol, uri: outcome.uri },
          ],
        }),
    });
    const outcomeTraceId = createTransactionTraceId();
    const outcomeMetadata = {
      ...outcomeStageMetadata,
      txTraceId: outcomeTraceId,
    };
    const outcomeOpts: SubmissionOptions = {
      ...baseOpts,
      instructionName: 'prepare_outcomes',
      traceId: outcomeTraceId,
      metadata: outcomeMetadata,
      expectedRelayFeePayer: relayFeePayerBase58,
      forceRelay: true,
    };
    const outcomeResult = await retryTransientSubmission({
      submitTransaction,
      buildResult: toEngineBuildResult(outcomeBuild),
      options: outcomeOpts,
      retryContext: { workflow: 'market_creation', workflowTraceId: marketCreationTraceId },
      signal,
      throwIfAborted,
      sleepOrAbort,
      alreadyLanded: async () => ((await provenPreparedBitmap()) & (1 << i)) !== 0,
      onRetryScheduled: ({ attempt, delayMs, result }) =>
        logger?.warn('market_creation.stage_retry_scheduled', {
          ...submissionFailureLog(outcomeMetadata, result),
          attempt,
          delayMs,
        }),
    });
    if (!outcomeResult.ok) externalPreparationBitmap |= await provenPreparedBitmap();
    if (!outcomeResult.ok && (externalPreparationBitmap & (1 << i)) === 0) {
      state = {
        ...state,
        status: 'failed',
        error: {
          stage: 'outcomes',
          message:
            outcomeResult.error.morfiError ??
            outcomeResult.error.userMessageKey ??
            `outcome ${String(i)} failed`,
        },
      };
      onProgress(state);
      logger?.error(
        'market_creation.outcome_failed',
        submissionFailureLog(outcomeMetadata, outcomeResult),
      );
      throw txSubmitError(
        `prepare_outcomes[${String(i)}] failed: ${outcomeResult.error.morfiError ?? outcomeResult.error.userMessageKey ?? outcomeResult.error.code}`,
        outcomeResult.error,
        submissionFailureLog(outcomeMetadata, outcomeResult),
      );
    }
    if (outcomeResult.ok) outcomeSignatures.push(outcomeResult.signature);
    state = { ...state, outcomesConfirmed: i + 1 };
    onProgress(state);
  }

  throwIfAborted(signal);
  const finalizeSignature = outcomeSignatures.at(-1) ?? null;
  // Readiness is committed by the last preparation; retain the UI completion fields.
  state = { ...state, status: 'finalize' };
  onProgress(state);
  state = {
    ...state,
    status: 'done',
    finalizeSubmitted: finalizeSignature,
    finalizeConfirmed: true,
  };
  onProgress(state);

  return {
    marketPubkey,
    shellSignature,
    outcomeSignatures,
    finalizeSignature,
    outcomeMintsBase58: outcomeMints.map((mint) => mint.toBase58()),
  };
}
